// APEX FieldProIQ integration panel + Forgot / Reset password (ADDITIVE)
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "./intelligence";

const API = process.env.REACT_APP_BACKEND_URL;
const tok = () => localStorage.getItem("apex_auth_token") || "";
const call = async (p, init = {}, auth = true) => {
  const r = await fetch(`${API}${p}`, {
    ...init,
    headers: { ...(init.headers || {}), "Content-Type": "application/json", ...(auth ? { Authorization: "Bearer " + tok() } : {}) },
  });
  if (!r.ok) {
    let m = "";
    try { m = (await r.json()).detail || ""; } catch {}
    throw new Error(m || `HTTP ${r.status}`);
  }
  return r.json();
};

const linkBtn = { background: "transparent", border: "none", color: "var(--cyan)", cursor: "pointer", fontSize: 11, padding: 0, textDecoration: "underline" };
const box = { border: "1px solid var(--border)", borderRadius: 6, padding: 12, background: "var(--panel, #fff)", marginBottom: 12 };
const statusColor = (s) => (s === "success" ? "#1E8449" : s === "FAILED" || s === "failed" ? "#C0392B" : "#B9770E");

// ─── Job page: FieldProIQ dispatch status + field reports ─────────────────
export function FieldProIQPanel({ jobId }) {
  const [st, setSt] = useState(null);
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setSt(await call(`/api/iv/fpiq/jobs/${jobId}/status`)); } catch { setSt(null); }
    try { setRep(await call(`/api/iv/fpiq/jobs/${jobId}/reports`)); } catch { setRep(null); }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setBusy(true);
    try {
      const r = await call(`/api/iv/fpiq/jobs/${jobId}/send`, { method: "POST" });
      const s = r.fieldproiq_dispatch_status;
      if (s === "success") toast("Sent to FieldProIQ");
      else toast(r.fieldproiq_dispatch_error || `FieldProIQ: ${s}`, "err");
      await load();
    } catch (e) { toast(e.message); }
    setBusy(false);
  };

  const download = async (type, reportId) => {
    try {
      const q = reportId ? `?report_id=${encodeURIComponent(reportId)}` : "";
      const r = await fetch(`${API}/api/iv/fpiq/jobs/${jobId}/reports/${type}/download${q}`, { headers: { Authorization: "Bearer " + tok() } });
      if (!r.ok) { let m = ""; try { m = (await r.json()).detail; } catch {} throw new Error(m || `HTTP ${r.status}`); }
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement("a"); a.href = url; a.download = `${jobId}_${type}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast(e.message); }
  };

  const retry = async () => {
    setBusy(true);
    try {
      const r = await call(`/api/iv/fpiq/outbox/${encodeURIComponent("dispatch:" + jobId)}/retry`, { method: "POST" });
      toast(r.status === "success" ? "Sent to FieldProIQ" : (r.error || `FieldProIQ: ${r.status}`), r.status === "success" ? "ok" : "err");
      await load();
    } catch (e) { toast(e.message, "err"); }
    setBusy(false);
  };

  const status = st?.fieldproiq_dispatch_status;
  const reports = rep?.reports || [];
  return (
    <div data-testid="fieldproiq-panel" style={box}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontFamily: "var(--rajdhani)", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", fontSize: 13 }}>FieldProIQ</div>
          <div style={{ fontSize: 11, color: "var(--muted)" }} data-testid="fieldproiq-request-id">
            Request: {st?.fieldproiq_request_id || "not sent yet"}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {status && (
            <span data-testid="fieldproiq-dispatch-status" style={{ fontSize: 11, fontWeight: 700, color: statusColor(status) }}>
              {status === "success" ? "SENT" : status.toUpperCase()}
            </span>
          )}
          {status === "FAILED" && (
            <button data-testid="fieldproiq-retry-btn" className="btn" disabled={busy} onClick={retry}
              style={{ fontSize: 11, padding: "6px 12px", border: "1px solid #C0392B", color: "#C0392B", background: "#fff", borderRadius: 4 }}>
              Retry
            </button>
          )}
          <button data-testid="fieldproiq-send-btn" className="btn btn-primary" disabled={busy} onClick={send} style={{ fontSize: 11, padding: "6px 12px" }}>
            {busy ? "Sending…" : st?.fieldproiq_request_id ? "Re-send to FieldProIQ" : "Send to FieldProIQ"}
          </button>
        </div>
      </div>
      {st?.fieldproiq_dispatch_error && status !== "success" && (
        <div data-testid="fieldproiq-dispatch-error" style={{ fontSize: 11, color: "#B9770E", marginTop: 6 }}>{st.fieldproiq_dispatch_error}</div>
      )}
      {status === "retrying" && st?.outbox?.next_retry_at && (
        <div data-testid="fieldproiq-next-retry" style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
          Auto-retry {(st.outbox.auto_retries || 0) + 1} of 5 at {new Date(st.outbox.next_retry_at).toLocaleString()}
        </div>
      )}
      <div style={{ marginTop: 10, fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)" }}>Field reports</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }} data-testid="fieldproiq-reports">
        {(rep?.report_types || []).map((t) => {
          const r = reports.filter((x) => x.type === t.type).slice(-1)[0];
          return (
            <button key={t.type} data-testid={`fieldproiq-report-${t.type}`} className="btn" disabled={!st?.fieldproiq_request_id}
              onClick={() => download(t.type, r?.report_id)}
              style={{ fontSize: 11, padding: "6px 10px", border: "1px solid var(--border, #ccc)", borderRadius: 4,
                background: r ? "#EAF6EE" : "var(--panel2, #f4f4f5)", color: r ? "#1E8449" : "var(--text, #333)",
                opacity: st?.fieldproiq_request_id ? 1 : 0.5, cursor: st?.fieldproiq_request_id ? "pointer" : "not-allowed" }}
              title={r ? `Ready ${r.created_at || ""}` : "Not reported ready yet"}>
              {t.label}{r ? " · ready" : ""}
            </button>
          );
        })}
      </div>
      {rep?.source && rep.source !== "live" && rep.source !== "stored" && (
        <div data-testid="fieldproiq-reports-source" style={{ fontSize: 10, color: "var(--muted)", marginTop: 6 }}>{rep.source}</div>
      )}
    </div>
  );
}

// ─── Login: "Forgot password?" ────────────────────────────────────────────
export function ForgotPasswordLink() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setBusy(true); setMsg("");
    try { const r = await call("/api/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) }, false); setMsg(r.message); }
    catch (er) { setMsg(er.message); }
    setBusy(false);
  };
  if (!open) return (
    <div style={{ textAlign: "right", marginBottom: 10 }}>
      <button type="button" data-testid="forgot-password-link" style={linkBtn} onClick={() => setOpen(true)}>Forgot password?</button>
    </div>
  );
  return (
    <div data-testid="forgot-password-form" style={{ ...box, marginTop: 4 }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: "var(--text)" }}>Reset your password</div>
      <input data-testid="forgot-password-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
        style={{ width: "100%", padding: 8, fontSize: 12, marginBottom: 8, boxSizing: "border-box", color: "#111", background: "#fff", border: "1px solid #bbb", borderRadius: 4 }} />
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" data-testid="forgot-password-submit" className="btn btn-primary" disabled={busy || !email} onClick={submit} style={{ fontSize: 11, padding: "6px 12px" }}>
          {busy ? "Sending…" : "Email reset link"}
        </button>
        <button type="button" data-testid="forgot-password-cancel" style={linkBtn} onClick={() => { setOpen(false); setMsg(""); }}>Cancel</button>
      </div>
      {msg && <div data-testid="forgot-password-message" style={{ fontSize: 11, marginTop: 8, color: "var(--muted)" }}>{msg}</div>}
    </div>
  );
}

// ─── Public page: /reset-password?token=… ─────────────────────────────────
export const shouldRenderResetPassword = () => window.location.pathname.replace(/\/+$/, "") === "/reset-password";

export function ResetPasswordPage() {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [state, setState] = useState("checking");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    call(`/api/auth/reset-password/validate?token=${encodeURIComponent(token)}`, {}, false)
      .then((r) => { setEmail(r.email); setState("form"); })
      .catch((e) => { setErr(e.message); setState("invalid"); });
  }, [token]);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.length < 8) return setErr("Password must be at least 8 characters");
    if (pw !== pw2) return setErr("Passwords do not match");
    try { await call("/api/auth/reset-password", { method: "POST", body: JSON.stringify({ token, new_password: pw }) }, false); setState("done"); }
    catch (er) { setErr(er.message); }
  };
  const inp = { width: "100%", padding: 10, fontSize: 13, marginBottom: 10, boxSizing: "border-box", border: "1px solid #ccc", borderRadius: 4 };
  return (
    <div data-testid="reset-password-page" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f4f4f5", fontFamily: "Arial, Helvetica, sans-serif" }}>
      <div style={{ width: 380, background: "#fff", borderRadius: 8, padding: 28, boxShadow: "0 8px 30px rgba(0,0,0,.08)", borderTop: "4px solid #C0392B" }}>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4, color: "#111" }}>Reset password</div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 18 }}>Apex Business Intelligence</div>
        {state === "checking" && <div data-testid="reset-password-checking">Checking link…</div>}
        {state === "invalid" && (
          <div data-testid="reset-password-invalid" style={{ fontSize: 13, color: "#C0392B" }}>
            {err || "This reset link is invalid or has expired."}
            <div style={{ marginTop: 14 }}><a href="/" style={{ color: "#C0392B" }} data-testid="reset-password-back">Back to sign in</a></div>
          </div>
        )}
        {state === "form" && (
          <form onSubmit={submit}>
            <div style={{ fontSize: 12, marginBottom: 12, color: "#111" }} data-testid="reset-password-email">Account: {email}</div>
            <input data-testid="reset-password-new" type="password" placeholder="New password (min 8 characters)" value={pw} onChange={(e) => setPw(e.target.value)} style={inp} />
            <input data-testid="reset-password-confirm" type="password" placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} style={inp} />
            {err && <div data-testid="reset-password-error" style={{ fontSize: 12, color: "#C0392B", marginBottom: 10 }}>{err}</div>}
            <button data-testid="reset-password-submit" type="submit" style={{ width: "100%", padding: 11, background: "#C0392B", color: "#fff", border: "none", borderRadius: 4, fontWeight: 700, cursor: "pointer" }}>Set new password</button>
          </form>
        )}
        {state === "done" && (
          <div data-testid="reset-password-done" style={{ fontSize: 13, color: "#111" }}>
            Your password has been updated. This link can't be used again.
            <div style={{ marginTop: 14 }}><a href="/" style={{ color: "#C0392B", fontWeight: 700 }} data-testid="reset-password-signin">Sign in</a></div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Login: register needs an invite now ──────────────────────────────────
export const InviteRequiredNote = () => (
  <div data-testid="register-invite-note" style={{ fontSize: 11, color: "var(--muted)", marginBottom: 10, lineHeight: 1.4 }}>
    New accounts need an invite from your company owner or admin. Open the invite link from your email to join.
  </div>
);

// ─── Bridge page: FieldProIQ deliveries that need attention ──────────────
export function FieldProIQOutboxPanel() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => { call("/api/iv/fpiq/outbox").then((d) => setItems(d.items || [])).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);
  const retry = async (id) => {
    try {
      const r = await call(`/api/iv/fpiq/outbox/${encodeURIComponent(id)}/retry`, { method: "POST" });
      toast(r.status === "success" ? "Delivered to FieldProIQ" : (r.error || r.status), r.status === "success" ? "ok" : "err");
    } catch (e) { toast(e.message, "err"); }
    load();
  };
  const open = items.filter((i) => i.status !== "success");
  return (
    <div data-testid="fieldproiq-outbox-panel" style={{ ...box, marginTop: 12 }}>
      <div style={{ fontFamily: "var(--rajdhani)", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", fontSize: 13, marginBottom: 6 }}>
        FieldProIQ deliveries ({open.length} open)
      </div>
      {open.length === 0 && <div style={{ fontSize: 11, color: "var(--muted)" }} data-testid="fieldproiq-outbox-empty">All deliveries succeeded.</div>}
      {open.map((i) => (
        <div key={i.id} data-testid={`fieldproiq-outbox-row-${i.id}`} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11, padding: "4px 0", borderTop: "1px solid var(--border)" }}>
          <span style={{ minWidth: 220, fontWeight: 700 }}>{i.id}</span>
          <span style={{ color: statusColor(i.status), fontWeight: 700, minWidth: 80 }}>{i.status}</span>
          <span style={{ flex: 1, color: "var(--muted)" }}>
            {i.status === "retrying" && i.next_retry_at ? `auto-retry ${(i.auto_retries || 0) + 1}/5 at ${new Date(i.next_retry_at).toLocaleString()} · ` : ""}{i.last_error}
          </span>
          {i.status === "FAILED" && (
            <button data-testid={`fieldproiq-outbox-retry-${i.id}`} className="btn" onClick={() => retry(i.id)} style={{ fontSize: 11, padding: "4px 10px" }}>Retry</button>
          )}
        </div>
      ))}
    </div>
  );
}
